/**
 * §7 — looking at a receipt without leaving the screen you were on.
 *
 * The operator's ask was specific: the attachment link in the queue's preview
 * pane, opening the PDF in an overlay, with a download option. Triage is a
 * decision about whether a transaction is worth coding, and a receipt is the
 * evidence for that decision — opening it in a new tab loses the cursor, the
 * filters and the position in a long queue.
 *
 * TWO GATES HAD TO MOVE BEFORE ANY OF THIS COULD WORK, AND BOTH ARE INVISIBLE
 * FROM HERE
 *
 *   1. `presignDownload` sets Content-Disposition: attachment. An <iframe>
 *      pointed at that URL DOWNLOADS the file and leaves an empty frame — no
 *      error, nothing in the console. Hence `attachmentUrl(id, 'inline')`,
 *      which is a different request for the same object.
 *
 *   2. The page's CSP was `default-src 'self'` with no `frame-src`, so an
 *      iframe to the bucket was blocked outright. `public/_headers` now names
 *      the bucket in `frame-src` and `headers.test.ts` guards it.
 *
 * Neither is visible in this file and neither fails loudly. They are the same
 * class as 2026-09-09's upload failure — the bucket, its CORS rule, the IAM
 * policy and the presigned POST were each verified correct while the page was
 * still declining to make the request.
 *
 * WHY AN IFRAME AND NOT <object> OR <embed>
 *
 * They would render the same PDF. They are also the legacy plugin surface, and
 * `object-src 'none'` in the CSP is what keeps that shut; using one here would
 * mean opening it. `frame-src` is one directive naming one bucket, which is
 * the smaller thing to have added.
 *
 * WHY NOT <img> FOR THE IMAGE RECEIPTS
 *
 * It would work and would need `img-src` extended to the bucket as well — a
 * second directive for the same host, and a second thing to keep in step with
 * a bucket rename. One frame renders every type the API accepts, so there is
 * one gate rather than two.
 *
 * WHAT `contentType` IS AND IS NOT TRUSTED FOR
 *
 * Since review issue #28 `recordAttachment` stores S3's own type, held to
 * ALLOWED_CONTENT_TYPES; a row recorded before then holds the label the CLIENT
 * sent. Either way S3 pins the OBJECT's type from the presign's condition, so
 * this file uses it to choose a message and nothing else. A mislabelled file renders whatever S3
 * says it is, which is a type the presign already allowed.
 */

import { useEffect, useRef, useState } from 'react';
import { InfoNote } from './InfoNote.js';
import { ApiError, api, type Attachment } from '../api.js';

/**
 * Types a browser will actually render in a frame.
 *
 * HEIC is deliberately absent, and it is not an oversight: the API accepts it
 * (a phone photo arrives as one), and no browser but Safari renders it. Framing
 * one produces a blank rectangle or a surprise download, so it gets the
 * download-only state and a sentence saying why — which is the honest answer
 * rather than a broken viewer.
 */
const RENDERABLE = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

function canRender(contentType: string): boolean {
  return RENDERABLE.includes(contentType.toLowerCase().split(';')[0]!.trim());
}

/**
 * The document itself: the signed URL, the frame, and the line about browsers
 * that download PDFs instead of showing them.
 *
 * EXTRACTED so the Invoices screen can put an invoice beside a date field while
 * the terms are read off it (2026-09-16). Everything difficult about rendering
 * one of these is in here -- the `inline` disposition without which an iframe
 * downloads the file and leaves an empty frame, the `frame-src` the CSP needs,
 * and the note that no script can detect a browser configured to download PDFs.
 * A second copy of that would be a second place to get it wrong.
 */
/**
 * Where a signed URL comes from.
 *
 * WHY THIS IS A PARAMETER NOW. The overlay's job is to render a document in a
 * frame and offer a download; which endpoint signs the URL is the caller's
 * business. Receipts come from `attachmentUrl`, bank statements from
 * `statementUrl` — the same file in the same bucket, reached through a
 * different row and a different permission check.
 *
 * Copying this component for statements was the alternative, and it would have
 * copied the three gates in the header above with it: the `inline` disposition,
 * the `frame-src` policy, and the browser setting that turns a frame into a
 * download. Those took a session to find once.
 */
export type ResolveDocumentUrl = (
  id: string,
  disposition: 'inline' | 'attachment',
) => Promise<{ url: string; filename: string }>;

const resolveAttachmentUrl: ResolveDocumentUrl = (id, disposition) =>
  api.attachmentUrl(id, disposition);

export function ReceiptFrame({
  attachment,
  resolveUrl = resolveAttachmentUrl,
}: {
  attachment: Attachment;
  resolveUrl?: ResolveDocumentUrl;
}) {
  const { src, error, renderable } = useAttachmentSrc(attachment, resolveUrl);

  return (
    <>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {!renderable && (
        <p className="muted overlay-note">
          {attachment.contentType} does not render in a browser outside Safari. Download
          it to look at it.
        </p>
      )}

      {renderable && src === null && !error && <p className="muted">Loading…</p>}

      {renderable && src !== null && (
        <>
          <iframe className="overlay-frame" src={src} title={attachment.originalFilename} />
          {/*
            FOLDED, BUT THE BUTTON IS ALWAYS THERE.

            CLAUDE.md requires this notice to be rendered rather than detected,
            because nothing in script can tell a cross-origin PDF from a
            cross-origin error page -- a browser set to download PDFs leaves an
            empty frame with no symptom anywhere. That requirement is about the
            answer being REACHABLE from the blank frame, and an info button
            sitting under it is reachable; the paragraph being permanent only
            taught the reader to skim past it.

            If a blank preview is ever reported again: open this note first,
            before reading the Content-Security-Policy.
          */}
          <p className="muted overlay-note">
            <InfoNote label="Nothing showing above?">
              Some browsers download PDFs instead of showing them. In Edge: Settings, Cookies
              and site permissions, PDF documents, then turn off &quot;Always download PDF
              files&quot;. Download works either way.
            </InfoNote>
          </p>
        </>
      )}
    </>
  );
}

/** The signed inline URL for one attachment, fetched once per receipt. */
function useAttachmentSrc(attachment: Attachment, resolveUrl: ResolveDocumentUrl) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const renderable = canRender(attachment.contentType);

  useEffect(() => {
    if (!renderable) return;
    let cancelled = false;
    setSrc(null);
    setError(null);
    resolveUrl(attachment.id, 'inline')
      .then((result) => {
        if (cancelled) return;
        if (typeof result.url !== 'string' || result.url === '') {
          setError('The server did not send a link for that receipt.');
          return;
        }
        setSrc(result.url);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(caught instanceof ApiError ? caught.message : 'Could not open that receipt.');
      });
    return () => {
      cancelled = true;
    };
  }, [attachment.id, renderable, resolveUrl]);

  return { src, error, renderable };
}

export function ReceiptOverlay({
  attachment,
  onClose,
  resolveUrl = resolveAttachmentUrl,
  label = 'Receipt',
}: {
  attachment: Attachment;
  onClose: () => void;
  /** Defaults to receipts; statements pass `api.statementUrl`. */
  resolveUrl?: ResolveDocumentUrl;
  /** What the dialog calls the document, for its accessible name. */
  label?: string;
}) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  /*
   * A DOWNLOAD failure is its own state, separate from the frame's.
   *
   * They fail for different reasons and mean different things: the frame not
   * rendering is "you cannot see it here", a refused download is "you cannot
   * take a copy". Sharing one variable would let the second overwrite the
   * first's message while the broken frame stayed on screen underneath it.
   */
  const [downloadError, setDownloadError] = useState<string | null>(null);

  /*
   * Escape closes, and the listener is on the window rather than on the dialog.
   *
   * Focus is inside the IFRAME as soon as the operator clicks the document, and
   * a key pressed there never reaches a React handler on any element of ours —
   * the frame is a separate document on a separate origin. A window listener
   * still fires while focus is anywhere in our own page, which is where it is
   * before the first click and after Tab.
   */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Focus lands on Close, so Escape is not the only way out for someone
  // arriving by keyboard, and so the queue's own j/k do not act on a list
  // hidden behind the overlay.
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  /**
   * Download is a SECOND request with the other disposition, not this URL
   * reused.
   *
   * Reusing the inline URL would open a tab that renders the PDF again instead
   * of saving it, which looks like the button doing nothing. window.open on an
   * attachment URL is a top-level navigation the browser turns into a save —
   * the same path the editor's receipt links take, and the one that needs no
   * CORS rule.
   */
  async function download() {
    try {
      const { url } = await resolveUrl(attachment.id, 'attachment');
      window.open(url, '_blank', 'noopener');
    } catch (caught) {
      setDownloadError(caught instanceof ApiError ? caught.message : 'Could not download that receipt.');
    }
  }

  return (
    <div
      className="overlay-backdrop"
      // A click on the backdrop closes; a click inside the panel must not.
      // Checked against the event's own target rather than with
      // stopPropagation on the panel, which would also swallow clicks the
      // panel's own controls need.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="overlay-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`${label} ${attachment.originalFilename}`}
      >
        <header className="row overlay-head">
          <h2>{attachment.originalFilename}</h2>
          <button type="button" className="link" onClick={() => void download()}>
            Download
          </button>
          <button type="button" className="link" ref={closeRef} onClick={onClose}>
            Close
          </button>
        </header>

        {downloadError && (
          <p role="alert" className="error">
            {downloadError}
          </p>
        )}

        {/*
          The document, and every hard-won detail of rendering one, is in
          ReceiptFrame -- shared with the Invoices screen's terms panel so there
          is one place that knows about the inline disposition and the CSP.
        */}
        <ReceiptFrame attachment={attachment} resolveUrl={resolveUrl} />
      </div>
    </div>
  );
}
